import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  TextField,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { APIHOST } from '../../common';
import { useApp } from '../AppContext';
import { getToken } from '../auth/AuthContext';

interface EditOrderHotelNotesDialogProps {
  open: boolean;
  onClose: () => void;
  orderId: string;
  initialNotes: string;
  onSaved?: () => void;
}

export default function EditOrderHotelNotesDialog({
  open,
  onClose,
  orderId,
  initialNotes,
  onSaved
}: EditOrderHotelNotesDialogProps) {
  const [notes, setNotes] = useState(initialNotes || '');
  const [saving, setSaving] = useState(false);
  const { setSnackbarInput } = useApp();

  useEffect(() => {
    if (open) {
      setNotes(initialNotes || '');
    }
  }, [open, initialNotes]);

  const handleSave = async () => {
    if (!orderId) {
      return;
    }

    try {
      setSaving(true);

      const token = getToken();
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${APIHOST}/api/orders/${orderId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          data: {
            notesForHotel: notes.trim() || null,
          }
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to save notes: ${response.status} ${response.statusText}`);
      }

      setSnackbarInput({ message: 'Hotel notes updated', severity: 'success' });
      onSaved?.();
      onClose();
    } catch (error) {
      setSnackbarInput({
        message: error instanceof Error ? error.message : 'Failed to update hotel notes',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Edit Notes for Hotel
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Notes for Hotel"
          fullWidth
          multiline
          minRows={6}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Enter notes to share with the hotel"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? <CircularProgress size={20} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
